import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AdvertiserPanel from "./AdvertiserPanel";
import ResponseLoader from "../../components/Loader/ResponseLoader";
import "./SSAICampaignCreate.css";

const SUPPORTED_MACROS = [
    { label: 'Aid', value: '[AD_ID]', description: 'Ad ID' },
    { label: 'Sid', value: '[SESSION_ID]', description: 'Session ID' },
    { label: 'Width', value: '[WIDTH]', description: 'Player width' },
    { label: 'Height', value: '[HEIGHT]', description: 'Player height' },
    { label: 'Ad Duration', value: '[DURATION]', description: 'Requested ad duration' },
    { label: 'Cachebuster', value: '[CACHEBUSTER]', description: 'Random string' },
    { label: 'Country', value: '[COUNTRY]', description: 'ISO country code' },
    { label: 'City', value: '[CITY]', description: 'City name' },
    { label: 'Zip Code', value: '[ZIP]', description: 'Zip/Postal code' },
    { label: 'DMA Code', value: '[DMA]', description: 'Nielsen DMA code' },
    { label: 'Ad Position', value: '[POSITION]', description: 'pre, mid, or post' },
    { label: 'User Agent', value: '[UA]', description: 'Browser user agent' },
    { label: 'User IP', value: '[IP]', description: 'Viewer IP' },
    { label: 'ADID', value: '[ADID]', description: 'Mobile advertising ID' },
    { label: 'OS', value: '[OS]', description: 'OS name' },
    { label: 'OS Version', value: '[OS_VER]', description: 'OS version' },
    { label: 'Language', value: '[LANG]', description: 'ISO language code' },
];

const SSAICampaignCreate = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("basic");
    const SSAI_API_URL = process.env.REACT_APP_SSAI_API_URL;

    const [form, setForm] = useState({
        name: "",
        description: "",
        vastTagUrl: "",
        integrationType: "tag",
        environment: "desktop",
        tagType: "VPAID Flash/JS",
        sensitiveSupply: false,
        pricingType: "revenue_share",
        shareOfRevenue: 56,
        adType: "mid_roll",
        type: "linear",
        budget: "",
        startDate: "",
        endDate: "",
        targeting: {
            geo: "",
            device: "",
            video_ids: ""
        }
    });

    const [generatedVast, setGeneratedVast] = useState("");

    useEffect(() => {
        // Mock generating the VAST tag text like in the screenshot
        const baseUrl = "http://s.adtelligent.com/?";
        const params = [
            `width=[replace_me]`,
            `height=[replace_me]`,
            `ua=[replace_me]`,
            `app_name=[replace_me]`,
            `vast_version=2`,
            `aid=916121`
        ];
        setGeneratedVast(baseUrl + params.join('&'));
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === "checkbox" ? checked : value;

        if (name.startsWith("targeting.")) {
            const field = name.split(".")[1];
            setForm(prev => ({
                ...prev,
                targeting: { ...prev.targeting, [field]: val }
            }));
        } else {
            setForm(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleInsertMacro = (macro) => {
        setForm(prev => ({
            ...prev,
            vastTagUrl: prev.vastTagUrl + macro
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Guard: VAST tag URL is always required
        const cleanUrl = (form.vastTagUrl || '').trim().replace(/\s+/g, '');
        if (!cleanUrl) {
            toast.error('VAST Tag URL is required. Please fill it in the Basic settings or Tag Constructor tab.');
            setActiveTab('basic');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...form,
                vastTagUrl: cleanUrl,
                budget: form.budget ? parseFloat(form.budget) : null,
                targeting: {
                    ...form.targeting,
                    video_ids: form.targeting.video_ids
                        ? form.targeting.video_ids.split(',').map(id => id.trim()).filter(id => id)
                        : []
                }
            };
            const response = await axios.post(`${SSAI_API_URL}/api/advertiser/campaigns`, payload, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });

            if (response.data && response.data.success) {
                toast.success('Source created successfully!');
                navigate('/ssai-campaigns');
            }
        } catch (error) {
            console.error('Error creating SSAI campaign:', error);
            const msg = error.response?.data?.errors?.[0]?.msg
                || error.response?.data?.message
                || 'Failed to create source.';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdvertiserPanel>
            <div className="ssai-create-container">
                {loading && <ResponseLoader />}

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="fw-bold text-dark">Create Source</h2>
                    <div className="badge bg-success px-3 py-2">Status: Enabled</div>
                </div>

                <div className="ssai-tabs">
                    <div className={`ssai-tab ${activeTab === "basic" ? "active" : ""}`} onClick={() => setActiveTab("basic")}>Basic settings</div>
                    <div className={`ssai-tab ${activeTab === "advanced" ? "active" : ""}`} onClick={() => setActiveTab("advanced")}>Advanced settings</div>
                    <div className={`ssai-tab ${activeTab === "constructor" ? "active" : ""}`} onClick={() => setActiveTab("constructor")}>Tag Constructor</div>
                    <div className="ssai-tab">Align Demand</div>
                    <div className="ssai-tab">External Statistics</div>
                </div>

                <form onSubmit={handleSubmit}>
                    {activeTab === "basic" && (
                        <div className="ssai-card">
                            <div className="row">
                                <div className="col-md-7">
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Source name*</label>
                                        <input type="text" name="name" value={form.name} onChange={handleChange} className="ssai-input" placeholder="e.g. Vast_test" required />
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Source description</label>
                                        <textarea name="description" value={form.description} onChange={handleChange} className="ssai-textarea" rows="3"></textarea>
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Type of Integration</label>
                                        <div className="d-flex flex-wrap gap-3 mt-2">
                                            {['tag', 'rtb', 'ad_push', 'pop', 'prebid', 'native', 'search'].map(t => (
                                                <div key={t} className="form-check">
                                                    <input className="form-check-input" type="radio" name="integrationType" value={t} checked={form.integrationType === t} onChange={handleChange} />
                                                    <label className="form-check-label text-capitalize">{t.replace('_', ' ')}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Environment</label>
                                        <select name="environment" value={form.environment} onChange={handleChange} className="ssai-select">
                                            <option value="desktop">Desktop</option>
                                            <option value="mobile">Mobile</option>
                                            <option value="ctv">CTV</option>
                                            <option value="app">App</option>
                                        </select>
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">VAST Tag URL <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            name="vastTagUrl"
                                            value={form.vastTagUrl}
                                            onChange={handleChange}
                                            className="ssai-input"
                                            placeholder="https://pubads.g.doubleclick.net/gampad/ads?..."
                                            required
                                        />
                                        <small className="text-muted">
                                            Paste your ad network VAST URL here. Macros like [UA], [AD_ID], [SESSION_ID] are supported.
                                        </small>
                                    </div>
                                    <div className="ssai-form-group">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" name="sensitiveSupply" checked={form.sensitiveSupply} onChange={handleChange} />
                                            <label className="form-check-label ms-2">Sensitive Supply</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-5">
                                    <div className="p-3 bg-light rounded border">
                                        <label className="ssai-label">Source pricing</label>
                                        <div className="form-check mb-2">
                                            <input className="form-check-input" type="radio" name="pricingType" value="revenue_share" checked={form.pricingType === "revenue_share"} onChange={handleChange} />
                                            <label className="form-check-label">Share of revenue</label>
                                            <div className="input-group input-group-sm mt-1" style={{ maxWidth: '120px' }}>
                                                <input type="number" name="shareOfRevenue" value={form.shareOfRevenue} onChange={handleChange} className="form-control" />
                                                <span className="input-group-text">%</span>
                                            </div>
                                        </div>
                                        <div className="form-check mb-2">
                                            <input className="form-check-input" type="radio" name="pricingType" value="floor_price" checked={form.pricingType === "floor_price"} onChange={handleChange} />
                                            <label className="form-check-label">Floor price</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" name="pricingType" value="fixed_cpm" checked={form.pricingType === "fixed_cpm"} onChange={handleChange} />
                                            <label className="form-check-label">Fixed CPM</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "advanced" && (
                        <div className="ssai-card">
                            <h5 className="mb-4">Targeting & Budget</h5>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Start Date</label>
                                        <input type="date" name="startDate" value={form.startDate} onChange={handleChange} className="ssai-input" />
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">End Date</label>
                                        <input type="date" name="endDate" value={form.endDate} onChange={handleChange} className="ssai-input" />
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Geography (ISO Codes)</label>
                                        <input type="text" name="targeting.geo" value={form.targeting.geo} onChange={handleChange} className="ssai-input" placeholder="US, UK, CA" />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Budget Limit ($)</label>
                                        <input type="number" name="budget" value={form.budget} onChange={handleChange} className="ssai-input" />
                                    </div>
                                    <div className="ssai-form-group">
                                        <label className="ssai-label">Device Targeting</label>
                                        <input type="text" name="targeting.device" value={form.targeting.device} onChange={handleChange} className="ssai-input" placeholder="Android, iOS, Tizen" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "constructor" && (
                        <div className="ssai-tag-constructor">
                            <div className="ssai-card">
                                <div className="ssai-form-group">
                                    <label className="ssai-label">VAST Tag URL Builder</label>
                                    <input
                                        type="text"
                                        name="vastTagUrl"
                                        value={form.vastTagUrl}
                                        onChange={handleChange}
                                        className="ssai-input mb-3"
                                        placeholder="Paste your base VAST tag here..."
                                    />
                                    <div className="ssai-url-display">
                                        {form.vastTagUrl || "Generated tag will appear here..."}
                                    </div>
                                </div>
                                <div className="mt-4 p-3 bg-white border rounded">
                                    <h6>Sample Result:</h6>
                                    <div className="text-muted small word-break">{generatedVast}</div>
                                </div>
                            </div>
                            <div className="ssai-macro-sidebar">
                                <h6 className="mb-3">General Macros</h6>
                                <div className="d-flex flex-wrap gap-2 mb-4">
                                    {SUPPORTED_MACROS.slice(0, 6).map(m => (
                                        <button key={m.value} type="button" className="ssai-macro-btn" onClick={() => handleInsertMacro(m.value)} title={m.description}>{m.label}</button>
                                    ))}
                                </div>
                                <h6 className="mb-3">Geo Macros</h6>
                                <div className="d-flex flex-wrap gap-2 mb-4">
                                    {SUPPORTED_MACROS.slice(6, 11).map(m => (
                                        <button key={m.value} type="button" className="ssai-macro-btn" onClick={() => handleInsertMacro(m.value)} title={m.description}>{m.label}</button>
                                    ))}
                                </div>
                                <h6 className="mb-3">Device Macros</h6>
                                <div className="d-flex flex-wrap gap-2">
                                    {SUPPORTED_MACROS.slice(11).map(m => (
                                        <button key={m.value} type="button" className="ssai-macro-btn" onClick={() => handleInsertMacro(m.value)} title={m.description}>{m.label}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-3 mt-2">
                        <button type="button" onClick={() => navigate("/ssai-campaigns")} className="btn btn-link text-muted text-decoration-none">Back to channels</button>
                        {activeTab !== "constructor" ? (
                            <button type="button" onClick={() => setActiveTab(activeTab === "basic" ? "advanced" : "constructor")} className="ssai-btn-primary px-5">Next Step</button>
                        ) : (
                            <button type="submit" className="ssai-btn-primary px-5">Create Source</button>
                        )}
                    </div>
                </form>
            </div>
        </AdvertiserPanel>
    );
};

export default SSAICampaignCreate;
